import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Handshake,
  TrendingUp,
  Users,
  Award,
  CheckCircle,
  Globe,
  ShieldCheck,
  Brain,
  Rocket,
  User,
  Users2,
  Layers,
  DollarSign,
  KanbanSquare,
  UserCheck,
  SlidersHorizontal,
  Network,
  Puzzle,
  Shield,
  Server,
  Plug,
} from "lucide-react";
import {
  WhatWeDoSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
  IndustriesSection,
} from "../components/CommonSections";
import { TEAM_IMAGES } from "@/data/teamImages";
import { CASE_STUDIES } from "@/data/mock";

const PartnersPage = () => {
  const partnerTypes = [
    {
      icon: Handshake,
      title: "Technology Alliance Partner",
      description:
        "We collaborate with global technology platforms, cloud providers, SaaS companies, and enterprise software vendors to implement, integrate, and optimize their solutions for end clients.",
      benefits: [
        "Certified implementation and customization",
        "Seamless system integration",
        "Performance optimization",
        "Ongoing support and upgrades",
        "Industry-specific configurations",
      ],
      businessModelDescription:
        "Implementation + integration revenue share or service-based billing.",
    },
    {
      icon: Globe,
      title: "Offshore Development Partner",
      description:
        "We act as a reliable offshore engineering arm for consulting firms, digital agencies, and product companies looking to scale delivery capacity cost-effectively.",
      benefits: [
        "Cost-efficient engineering teams",
        "Time-zone aligned collaboration",
        "Transparent sprint-based delivery",
        "Agile project management",
        "Long-term scalability",
      ],
      businessModelDescription:
        "Monthly retainer / resource-based billing / milestone-based projects.",
    },
    {
      icon: ShieldCheck,
      title: "White-Label Engineering Partner",
      description:
        "We build and deliver projects under your brand name while maintaining complete confidentiality and delivery excellence.",
      benefits: [
        "NDA-backed engagement",
        "Zero client visibility of offshore structure (if required)",
        "Custom branding support",
        "Scalable engineering capacity",
        "Dedicated project managers",
      ],

      businessModelDescription:
        "Fixed cost, T&M, or retainer-based engineering support.",
    },
    {
      icon: Brain,
      title: "AI Transformation Partner",
      description:
        "We help organizations integrate AI into their products, workflows, and business processes to drive automation, intelligence, and competitive advantage.",
      benefits: [
        "AI roadmap consulting",
        "LLM integration (ChatGPT, Azure OpenAI, etc.)",
        "AI-powered chatbots & automation",
        "Predictive analytics solutions",
        "AI-enhanced SaaS products",
        "Workflow automation using AI",
      ],

      businessModelDescription:
        "Consulting + implementation + long-term AI optimization contracts.",
    },
    {
      icon: Rocket,
      title: "Startup Technology Partner",
      description:
        "We act as a long-term technology co-builder for startups — from MVP development to scaling, optimization, and investor readiness.",
      benefits: [
        "MVP architecture & rapid prototyping",
        "Product-market fit iteration support",
        "Scalable cloud architecture",
        "Investor demo readiness",
        "CTO-as-a-Service",
        "Long-term product roadmap execution",
      ],

      businessModelDescription:
        "Project-based / equity + reduced cost (selectively) / milestone-based engagement.",
    },
    {
      icon: Users2,
      title: "Dedicated Engineering Teams",
      description:
        "We provide fully dedicated, remote engineering teams that function as an extension of your in-house team.",
      benefits: [
        "Pre-vetted developers",
        "Long-term team stability",
        "Direct communication access",
        "Agile sprint-based execution",
        "Flexible scaling (up/down)",
        "Transparent reporting",
      ],

      businessModelDescription:
        "Monthly per-resource billing with flexible contract terms.",
    },
  ];

  const logos = [
    { name: "BKL", image: TEAM_IMAGES.bkl, url: "https://bigkittylabs.com/" },
    { name: "UESIO", image: TEAM_IMAGES.uesio, url: "https://ues.io/" },
    {
      name: "Bitwise",
      image: TEAM_IMAGES.bitwise,
      url: "https://www.bitwiseglobal.com/",
    },
  ];

  const programBenefits = [
    {
      icon: TrendingUp,
      title: "Revenue Growth Opportunities",
      benefits: [
        "Access to new markets (US, Europe, APAC)",
        "Joint proposal submissions",
        "Revenue-sharing models",
        "Recurring revenue through long-term delivery contracts",
        "Upsell & cross-sell technical capabilities",
      ],
      impact: "Expand service portfolio without increasing fixed overhead.",
    },
    {
      icon: Layers,
      title: "Scalable Delivery Capacity",
      benefits: [
        "Rapid team ramp-up (2–6 weeks)",
        "Pre-vetted full-stack, AI, DevOps, QA engineers",
        "Elastic team scaling (scale up/down on demand)",
        "Long-term dedicated resources",
      ],
      impact: "Meet aggressive client deadlines without hiring risk.",
    },
    {
      icon: DollarSign,
      title: "Cost Efficiency & Margin Expansion",

      benefits: [
        "Competitive offshore engineering cost structure",
        "Higher profit margins on white-label delivery",
        "Reduced recruitment & infrastructure costs",
        "Optimized blended delivery model",
      ],

      impact: "Improve EBITDA margins while maintaining premium pricing.",
    },
    {
      icon: Brain,
      title: "AI & Innovation Enablement",

      benefits: [
        "AI roadmap consulting support",
        "LLM integrations (OpenAI, Azure OpenAI, etc.)",
        "Automation frameworks",
        "AI-driven SaaS feature development",
        "AI enhancement of legacy systems",
      ],

      impact: "Add AI-powered services without building internal AI teams.",
    },
    {
      icon: ShieldCheck,
      title: "Confidential & Brand-Safe Engagement",

      benefits: [
        "NDA-backed delivery",
        "White-label execution",
        "No direct client solicitation policy",
        "Flexible engagement visibility (transparent or invisible delivery)",
      ],

      impact: "Full control of client relationship remains with partner.",
    },
    {
      icon: KanbanSquare,
      title: "Agile & Transparent Delivery Governance",

      benefits: [
        "Sprint-based execution",
        "Real-time project dashboards",
        "Weekly reporting & KPI tracking",
        "Risk management protocols",
        "Dedicated project managers",
      ],
      impact: "Predictable, accountable, enterprise-grade execution.",
    },
    {
      icon: UserCheck,
      title: "Dedicated Account Management",
      benefits: [
        "Assigned Partner Success Manager",
        "Quarterly strategy alignment calls",
        "Growth roadmap collaboration",
        "Technical pre-sales support",
      ],
      impact: "Long-term strategic alignment, not transactional engagement.",
    },
    {
      icon: Rocket,
      title: "Faster Time-to-Market",
      benefits: [
        "MVP delivery in 6–12 weeks (for startups)",
        "Rapid POC development",
        "Pre-built solution accelerators",
        "DevOps automation pipelines",
      ],
      impact: "Win competitive bids with faster launch timelines",
    },
    {
      icon: SlidersHorizontal,
      title: "Flexible Engagement Models",

      benefits: [
        "Fixed price",
        "Time & Material",
        "Monthly retainer",
        "Dedicated teams",
        "Revenue-share model (for startups)",
        "Hybrid commercial structures",
      ],

      impact: "Tailored engagement based on risk appetite and growth strategy.",
    },
    {
      icon: Network,
      title: "Access to Cross-Industry Expertise",

      benefits: [
        "AI & Automation",
        "SaaS Platforms",
        "Enterprise Applications",
        "Mobile & Web Applications",
        "Cloud Migration & DevOps",
      ],

      impact: "Broaden your solution capability instantly.",
    },
    {
      icon: Puzzle,
      title: "Technology Ecosystem Alignment",

      benefits: [
        "Amazon Web Services",
        "Microsoft Azure",
        "Google Cloud Platform",
        "OpenAI",
      ],

      impact: "Enterprise-ready architecture & credibility.",
    },
    {
      icon: Shield,
      title: "Risk Mitigation & Operational Stability",

      benefits: [
        "Backup resource planning",
        "Knowledge redundancy",
        "Process documentation",
        "IP protection standards",
        "Secure code & infrastructure policies",
      ],
      impact: "Reduced delivery risk for global clients.",
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b32126] font-semibold">Partnership</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Partner With Us
            </h1>
            <p className="text-xl text-gray-300">
              We're a delivery-first engineering partner built for agencies,
              product companies, and consultancies that need reliable offshore
              capacity, AI expertise, and white-label execution at scale.
            </p>
          </div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Partner With Infotech.Works?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Because you get pre-vetted engineering teams, sprint-based
              delivery, NDA-backed white-label execution, and AI capabilities —
              without the overhead of hiring, managing, or scaling an internal
              team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnerTypes.map((type, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                  <type.icon className="h-7 w-7 text-[#b32126]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  How We Add Value
                </h3>
                <ul className="space-y-2 list-disc list-inside">
                  {type.benefits.map((benefit, idx) => (
                    <li key={idx} className="gap-2 text-sm text-gray-600">
                      {/* <CheckCircle className="h-4 w-4 text-[#b32126]" /> */}
                      {benefit}
                    </li>
                  ))}
                </ul>

                <h3 className="text-sm font-semibold text-gray-900 mt-3 mb-3">
                  Business Model
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {type.businessModelDescription}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Partners */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Our Clients & Partners
            </h2>
            <p className="text-gray-600">
              We work with leading technology companies to deliver best-in-class
              solutions
            </p>
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            {logos.map((logo, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm flex items-center justify-center h-24"
              >
                <a
                  href={logo.url}
                  target="_blank"
                  className="w-full h-full flex items-center justify-center"
                >
                  <img
                    src={logo.image}
                    alt={logo.name}
                    className="w-full h-full object-contain"
                  />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Partner Benefits */}
      {/* <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Partner Program Benefits
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <CheckCircle className="h-6 w-6 text-[#b32126] flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Revenue Growth
                    </h4>
                    <p className="text-gray-600">
                      Access new revenue streams through referrals and joint
                      opportunities
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <CheckCircle className="h-6 w-6 text-[#b32126] flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Technical Resources
                    </h4>
                    <p className="text-gray-600">
                      Get access to our technical documentation, APIs, and
                      support team
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <CheckCircle className="h-6 w-6 text-[#b32126] flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Marketing Support
                    </h4>
                    <p className="text-gray-600">
                      Co-marketing opportunities, case studies, and joint events
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <CheckCircle className="h-6 w-6 text-[#b32126] flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      Training & Certification
                    </h4>
                    <p className="text-gray-600">
                      Comprehensive training programs to build expertise
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Partnership"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section> */}

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Infotech.Works Partner Program — Benefits
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {programBenefits.map((type, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6">
                  <type.icon className="h-7 w-7 text-[#b32126]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {type.title}
                </h3>

                <ul className="space-y-2 list-disc list-inside">
                  {type.benefits.map((benefit, idx) => (
                    <li key={idx} className="gap-2 text-sm text-gray-600">
                      {/* <CheckCircle className="h-4 w-4 text-[#b32126]" /> */}
                      {benefit}
                    </li>
                  ))}
                </ul>

                <h3 className="text-sm font-semibold text-gray-900 mt-3 mb-3">
                  Impact
                </h3>
                <p className="text-gray-600 text-sm mb-4">{type.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Sections */}
      <WhatWeDoSection />
      <IndustriesSection />
      {/* Success Stories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's explore how we can create value together.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Become a Partner
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
